import React, { useState } from 'react';
import { Grid, Dropdown as DropdownNativo, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import SectionLabel from '../../../components/SectionLabel';
import { FieldLabel } from '../../../styles';

type BranchEstateOption = {
  key: string;
  text: string;
  value: string;
  city: string;
  state: string;
};

type BranchEstatesSectionProps = {
  branchEstateOptions: BranchEstateOption[];
  branchEstateIds: string[];
  setFieldValue: (field: string, value: any) => void;
};

const DescriptionText = styled.p`
  color: #6b7280;
  font-size: 14px;
  margin: 0;
`;

const CardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
`;

const BranchCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px 12px;
  min-width: 160px;
  background: #fff;
  position: relative;
`;

const CardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: #f3eeff;
  border-radius: 6px;
  flex-shrink: 0;

  i.icon {
    color: #59249b;
    margin: 0 !important;
  }
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const CardName = styled.span`
  font-weight: bold;
  font-size: 14px;
  color: #364445;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardLocation = styled.span`
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  i.icon {
    color: #db2828;
    margin: 0 !important;
    font-size: 16px;
  }

  &:hover i.icon {
    color: #a50000;
  }
`;

export const BranchEstatesSection = ({
  branchEstateOptions,
  branchEstateIds,
  setFieldValue
}: BranchEstatesSectionProps) => {
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);

  const selectedBranches = branchEstateOptions.filter(opt =>
    branchEstateIds.includes(opt.value)
  );

  const availableOptions = branchEstateOptions.filter(
    opt => !branchEstateIds.includes(opt.value)
  );

  const handleAdd = (_e: any, data: any) => {
    const { value } = data;
    if (typeof value === 'string' && !branchEstateIds.includes(value)) {
      setFieldValue('branchEstateIds', [...branchEstateIds, value]);
    }
    setSearchValue(undefined);
  };

  const handleRemove = (id: string) => {
    setFieldValue(
      'branchEstateIds',
      branchEstateIds.filter(bid => bid !== id)
    );
  };

  const formatLocation = (city: string, state: string) => {
    if (city && state) return `${city} - ${state}`;
    return city || state || '';
  };

  return (
    <>
      <SectionLabel text="QUADRO DE FILIAIS" />

      <Grid.Row>
        <Grid.Column width={16}>
          <DescriptionText>
            Adicione aqui as imobiliárias filiadas à Matriz. Usuários com perfil de administrador
            poderão acessar e operar o painel de uma filial a partir de um único usuário.
          </DescriptionText>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={6}>
          <FieldLabel>Buscar filial</FieldLabel>
          <DropdownNativo
            placeholder="Selecione"
            value={searchValue}
            options={availableOptions}
            search
            selection
            clearable
            fluid
            onChange={handleAdd}
          />
        </Grid.Column>
      </Grid.Row>

      {selectedBranches.length > 0 && (
        <Grid.Row>
          <Grid.Column width={16}>
            <CardsWrapper>
              {selectedBranches.map(branch => (
                <BranchCard key={branch.value}>
                  <CardIcon>
                    <Icon name="building" />
                  </CardIcon>
                  <CardInfo>
                    <CardName title={branch.text}>{branch.text}</CardName>
                    <CardLocation>{formatLocation(branch.city, branch.state)}</CardLocation>
                  </CardInfo>
                  <RemoveButton
                    type="button"
                    title="Remover filial"
                    onClick={() => handleRemove(branch.value)}
                  >
                    <Icon name="times circle" />
                  </RemoveButton>
                </BranchCard>
              ))}
            </CardsWrapper>
          </Grid.Column>
        </Grid.Row>
      )}
    </>
  );
};
